import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { getServerURL } from '../config/api';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [esp32Devices, setEsp32Devices] = useState([]);
  const [latestScan, setLatestScan] = useState(null);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [scanBuffer, setScanBuffer] = useState({});
  const socketRef = useRef(null);

  // Server URL is now imported from centralized config

  // Function to check if we have complete scan data
  const isCompleteScanData = (scanData) => {
    return scanData &&
      scanData.barcodeData &&
      scanData.barcodeData.trim().length > 0 &&
      scanData.deviceName &&
      scanData.scanType;
  };

  // Function to auto-save scan data to database
  const autoSaveScanToDatabase = async (scanData) => {
    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/barcodes/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          barcodeData: scanData.barcodeData,
          deviceName: scanData.deviceName || 'ESP32 Scanner',
          deviceId: scanData.deviceId || 'unknown',
          scanType: scanData.scanType || 'unknown',
          source: 'ESP32',
          productName: scanData.productInfo?.productName || `Scanned Product ${scanData.barcodeData}`,
          productId: scanData.barcodeData,
          price: 0,
          locationX: 0,
          locationY: 0,
          locationZ: 0,
          category: scanData.productInfo?.productType || 'Scanned',
          metadata: {
            deviceName: scanData.deviceName || 'ESP32 Scanner',
            deviceId: scanData.deviceId || 'unknown',
            scanType: scanData.scanType || 'unknown',
            timestamp: scanData.timestamp || new Date().toISOString(),
            productDetails: scanData.productInfo?.productDetails || '',
            foundInLocalDB: scanData.productInfo?.foundInLocalDB || false,
            autoSaved: true,
            source: 'live_scanner'
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Live Scanner result auto-saved to database:', result);
        return result;
      } else {
        console.error('Failed to auto-save Live Scanner result:', response.statusText);
      }
    } catch (error) {
      console.error('Error auto-saving Live Scanner result:', error);
    }
  };

  // Function to buffer and process scan data
  const processScanData = (scanData, eventType) => {
    console.log(`🔄 Processing ${eventType}:`, scanData);
    console.log('📊 AI Analysis in scan data:', scanData.aiAnalysis);
    console.log('🔍 Scan completeness check:', {
      hasBarcodeData: !!(scanData.barcodeData && scanData.barcodeData.trim().length > 0),
      hasDeviceName: !!scanData.deviceName,
      hasScanType: !!scanData.scanType,
      source: scanData.source,
      scanType: scanData.scanType
    });

    // Only process ESP32 scans - check by deviceName and source
    const isEsp32Device = scanData.deviceName?.includes('Scanner') ||
      scanData.deviceName?.includes('RobridgeAI') ||
      scanData.deviceName?.includes('Robridge') ||  // Added support for "Robridge" devices
      scanData.deviceName?.includes('ESP32-') ||  // Added support for auto-registered devices
      scanData.source === 'esp32' ||
      scanData.source === 'esp32_basic' ||  // Added support for basic scans
      scanData.source === 'ESP32_LIVE_SCANNER';

    if (!isEsp32Device) {
      console.log('❌ Skipping non-ESP32 scan:', { source: scanData.source, deviceName: scanData.deviceName });
      return;
    }

    console.log('✅ Processing ESP32 scan:', { deviceName: scanData.deviceName, scanType: scanData.scanType });

    // Buffer the data - make sure to preserve aiAnalysis
    setScanBuffer(prev => ({
      ...prev,
      ...scanData,
      aiAnalysis: scanData.aiAnalysis || prev.aiAnalysis, // Preserve AI analysis
      lastUpdate: Date.now(),
      eventType: eventType
    }));

    // Check if we have complete data after a short delay
    setTimeout(() => {
      setScanBuffer(currentBuffer => {
        console.log('🔍 Checking scan completeness:', {
          isComplete: isCompleteScanData(currentBuffer),
          isProcessing: isProcessingScan,
          bufferData: currentBuffer
        });

        if (isCompleteScanData(currentBuffer) && !isProcessingScan) {
          console.log('✅ Complete scan data found, processing...');
          setIsProcessingScan(true);

          // Validate timestamp - if it's 1970 (from millis()) or invalid, use current time
          const hasValidTimestamp = (ts) => {
            if (!ts) return false;
            const date = new Date(ts);
            return !isNaN(date.getTime()) && date.getFullYear() > 2020;
          };

          const validTimestamp = hasValidTimestamp(currentBuffer.timestamp)
            ? currentBuffer.timestamp
            : Date.now();

          const completeScan = {
            ...currentBuffer,
            timestamp: validTimestamp,
            source: 'ESP32', // Ensure source is set to ESP32 for saving
            aiAnalysis: currentBuffer.aiAnalysis, // Explicitly include AI analysis
            dbRecord: currentBuffer.productInfo ? {
              id: currentBuffer.barcodeData,
              name: currentBuffer.productInfo.productName,
              category: currentBuffer.productInfo.productType,
              price: '$0.00',
              location: 'ESP32 Scanner',
              lastUpdated: validTimestamp,
              status: currentBuffer.productInfo.foundInLocalDB ? 'ACTIVE' : 'UNKNOWN'
            } : null
          };

          console.log('Setting latest scan with AI analysis:', completeScan.aiAnalysis);
          setLatestScan(completeScan);

          // Auto-save disabled - user must click "Save This Scan" button manually
          autoSaveScanToDatabase(completeScan);

          // Reset processing state
          setTimeout(() => {
            setIsProcessingScan(false);
            setScanBuffer({});
          }, 2000);
        }
        return currentBuffer;
      });
    }, 500); // Wait 500ms for all data to arrive
  };

  useEffect(() => {

    // Create WebSocket connection
    const serverURL = getServerURL();

    socketRef.current = io(serverURL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    // Connection event handlers
    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);

      // Authenticate with token
      const token = localStorage.getItem('robridge_token');
      if (token) {
        console.log('🔐 Authenticating WebSocket...');
        socketRef.current.emit('authenticate', token);
      } else {
        console.log('⚠️ No token found for WebSocket authentication');
      }
    });

    socketRef.current.on('authenticated', async (data) => {
      console.log('✅ WebSocket authenticated successfully:', data);

      // Fetch devices ONLY after authentication
      const fetchDevices = async () => {
        try {
          const serverURL = getServerURL();
          const token = localStorage.getItem('robridge_token');

          // 1. Fetch ALL paired devices from DB (Persistent)
          const dbPromise = fetch(`${serverURL}/api/devices`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json()).catch(() => ({ success: false }));

          // 2. Fetch LIVE devices from Memory (Real-time details like IP)
          const livePromise = fetch(`${serverURL}/api/esp32/devices`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json()).catch(() => ({ success: false }));

          const [dbData, liveData] = await Promise.all([dbPromise, livePromise]);

          if (dbData.success) {
            // Helper logic duplicated here since we can't easily hoist it out of useEffect in this specific file structure without bigger refactor
            // Ideally move mergeDevices out, but for now inline is safe.
            const mergeDevicesInternal = (dbDevices, liveDevices) => {
              const liveMap = new Map();
              if (liveDevices) {
                liveDevices.forEach(d => liveMap.set(d.deviceId, d));
              }
              return dbDevices.map(dbDev => {
                const baseDevice = {
                  deviceId: dbDev.device_id,
                  deviceName: dbDev.device_name,
                  lastSeen: dbDev.last_seen,
                  isActive: dbDev.is_active,
                  status: 'disconnected',
                  ipAddress: 'Offline',
                  firmwareVersion: 'Unknown',
                  totalScans: 0
                };

                const liveDev = liveMap.get(dbDev.device_id);
                const lastSeenDate = new Date(dbDev.last_seen);
                const fiveMinsAgo = new Date(Date.now() - 5 * 60 * 1000);
                const isRecentlySeen = lastSeenDate > fiveMinsAgo;

                if (liveDev) {
                  return { ...baseDevice, ...liveDev, status: liveDev.status || (isRecentlySeen ? 'connected' : 'disconnected') };
                } else if (isRecentlySeen) {
                  return { ...baseDevice, status: 'connected', ipAddress: 'Active' };
                }
                return baseDevice;
              });
            };

            const mergedDevices = mergeDevicesInternal(dbData.devices, liveData.success ? liveData.devices : []);
            setEsp32Devices(mergedDevices);
          }
        } catch (error) {
          console.error('Error fetching ESP32 devices:', error);
        }
      };

      // Expose for event handlers
      socketRef.current.fetchDevices = fetchDevices;

      // Initial fetch
      await fetchDevices();

      // Set up 1-second polling for fast updates (fallback if WebSocket events don't fire)
      const devicePollingInterval = setInterval(fetchDevices, 1000);

      // Store interval ID for cleanup
      socketRef.current.devicePollingInterval = devicePollingInterval;
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setIsConnected(false);
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Device Connected specific event handlers
    socketRef.current.on('esp32_devices_update', (devices) => {
      console.log('ESP32 devices updated event received, triggering full refresh');
      // IGNORE the payload 'devices' because it only contains ONLINE devices.
      // We need to merge online status with the persistent DB list of paired devices.
      if (socketRef.current.fetchDevices) {
        socketRef.current.fetchDevices();
      }
    });

    socketRef.current.on('esp32_barcode_scan', (scanData) => {
      console.log('📡 Received esp32_barcode_scan event:', scanData);
      processScanData(scanData, 'esp32_barcode_scan');
    });

    socketRef.current.on('esp32_scan_processed', (scanData) => {
      console.log('📡 Received esp32_scan_processed event:', scanData);
      processScanData(scanData, 'esp32_scan_processed');
    });

    socketRef.current.on('esp32_device_connected', (device) => {
      console.log('New ESP32 device connected:', device);
      setEsp32Devices(prev => [...prev.filter(d => d.deviceId !== device.deviceId), device]);
    });

    // Helper to merge DB and Live devices
    const mergeDevices = (dbDevices, liveDevices) => {
      const liveMap = new Map();
      if (liveDevices) {
        liveDevices.forEach(d => liveMap.set(d.deviceId, d));
      }

      return dbDevices.map(dbDev => {
        // Map snake_case DB fields to camelCase expected by contexts
        const baseDevice = {
          deviceId: dbDev.device_id,
          deviceName: dbDev.device_name,
          lastSeen: dbDev.last_seen,
          isActive: dbDev.is_active,
          // Defaults
          status: 'disconnected',
          ipAddress: 'Offline',
          firmwareVersion: 'Unknown',
          totalScans: 0
        };

        const liveDev = liveMap.get(dbDev.device_id);

        // Check if recently seen (within 5 mins) even if liveDev is missing/disconnected
        const lastSeenDate = new Date(dbDev.last_seen);
        const fiveMinsAgo = new Date(Date.now() - 5 * 60 * 1000);
        const isRecentlySeen = lastSeenDate > fiveMinsAgo;

        if (liveDev) {
          return {
            ...baseDevice,
            ...liveDev,
            status: liveDev.status || (isRecentlySeen ? 'connected' : 'disconnected')
          };
        } else if (isRecentlySeen) {
          // If we don't have live data but DB says it was just seen, assume connected
          return {
            ...baseDevice,
            status: 'connected',
            ipAddress: 'Active' // We don't have actual IP but we know it's there
          };
        }

        return baseDevice;
      });
    };

    // Listen for device pairing events to instantly update device list
    socketRef.current.on('device_paired', async (data) => {
      console.log('🔗 Device paired event received:', data);
      // Re-trigger the main fetch to ensure consistent merged state
      if (socketRef.current.fetchDevices) {
        socketRef.current.fetchDevices();
      }
    });

    // Listen for device unpairing events
    socketRef.current.on('device_unpaired', async (data) => {
      console.log('🔓 Device unpaired event received:', data);
      // Re-trigger the main fetch to ensure consistent merged state
      if (socketRef.current.fetchDevices) {
        socketRef.current.fetchDevices();
      }
    });

    // Fetch initial Device Connected devices - DISABLED: Now fetched after WebSocket authentication
    // Devices are now fetched in the 'authenticated' event handler above

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        // Clear polling interval if it exists
        if (socketRef.current.devicePollingInterval) {
          clearInterval(socketRef.current.devicePollingInterval);
        }
        socketRef.current.disconnect();
      }
    };
  }, []);

  const value = {
    isConnected,
    esp32Devices,
    latestScan,
    setLatestScan,
    isProcessingScan,
    socket: socketRef.current
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
